// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the ranking behind Home's "Sorted" list and the "conversations you can
// clear" card. Pure, so the rules are unit tested without stores. Each source
// (Konvo DMs, Teams-bridged chats, WhatsApp, Outlook mail, ClickUp) is reduced
// to the same HomeFeedInput shape by the component; this file only decides
// what is worth your attention first and what can go. Shared with the API, which
// ranks the "Waiting on you" home-screen widget the same way.

export type HomeFeedSource = 'konvo' | 'teams' | 'whatsapp' | 'mail' | 'clickup';

export interface HomeFeedInput {
	/** Stable key, unique across sources. */
	key: string;
	source: HomeFeedSource;
	name: string;
	preview: string;
	/** Epoch ms of the newest activity, or null when unknown. */
	lastAt: number | null;
	unread: number;
	mention?: boolean;
	muted?: boolean;
	/** The reader flagged it, or the sender asked for an answer. */
	flagged?: boolean;
	/** The last message in the conversation came from the reader. */
	fromMe?: boolean;
	/** Group chats and channels: a quiet muted group is clutter, a muted DM is still a person. */
	group?: boolean;
	/** Sign-in alerts, verification codes, password resets. */
	security?: boolean;
	/** Newsletters and marketing mail. */
	newsletter?: boolean;
	/** Calendar responses, tool notifications, out-of-office replies. */
	automated?: boolean;
	/** Already read: only clutter if it is still lying around (ClickUp notifications). */
	read?: boolean;
	/** The assistant's conversation id (konvo:, teams:, whatsapp:) when the row is a chat it can read or message. */
	conversationId?: string;
	/** Provider message id used for a direct Outlook reply draft. */
	messageId?: string;
}

export type HomeFeedTag =
	| {kind: 'mention'}
	| {kind: 'needs_reply'}
	| {kind: 'waiting'; days: number}
	| {kind: 'security'};

export interface HomeFeedItem extends HomeFeedInput {
	score: number;
	tags: Array<HomeFeedTag>;
}

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

/** Older than this and a read conversation is no longer "recent" for the list. */
export const RECENT_WINDOW_MS = 3 * DAY_MS;
/** Waiting on you this long earns a "Waiting N days" tag. */
export const WAITING_TAG_MIN_DAYS = 3;
/** Fewer candidates than this and the clear card stays hidden. */
export const CLEAR_CARD_MIN = 3;

const NEWSLETTER_LOCAL_PART =
	/^(no-?reply|noreply|donotreply|do-not-reply|newsletters?|news|marketing|promo(tions)?|offers|hello|info|mailer|bounce|digest|updates?|team|community|campaigns?)([+.-]|$)/i;
const NEWSLETTER_TEXT = /\bunsubscribe\b|\bview (this|it) in (your )?browser\b|\bmanage (your )?preferences\b/i;
const AUTOMATED_SUBJECT =
	/^(re:\s*)?(accepted|declined|tentative(ly accepted)?|canceled|cancelled|updated invitation|invitation|automatic reply|out of office|autoreply|auto-reply|delivery status notification|undeliverable)\b/i;
const AUTOMATED_SENDER =
	/(^|[.@-])(github|gitlab|atlassian|jira|clickup|notifications?|alerts?|mailer-daemon|postmaster|calendar|teams|zoom|slack|linear|sentry|datadog|pagerduty|aws|amazonses|billing|receipts?|invoices?|statements?)([.@-]|$)/i;
const SECURITY_TEXT =
	/\b(new (device|sign[- ]?in|login)|signed in|sign[- ]?in (attempt|alert)|login (attempt|alert)|verification code|verify your|one[- ]time (code|password)|security (alert|code|notice)|password (reset|changed|was changed)|two[- ]factor|2fa|unusual activity|suspicious)\b/i;

/** Newsletters and marketing: a mailing-list style sender or the tell-tale footer. */
export function isNewsletterMail(input: {from_address: string | null; subject: string; preview: string}): boolean {
	const address = input.from_address?.trim().toLowerCase() ?? '';
	const localPart = address.split('@')[0] ?? '';
	if (NEWSLETTER_LOCAL_PART.test(localPart)) return true;
	return NEWSLETTER_TEXT.test(`${input.subject}\n${input.preview}`);
}

/** Calendar responses, tool notifications, bounces and out-of-office replies. */
export function isAutomatedMail(input: {from_address: string | null; subject: string}): boolean {
	if (AUTOMATED_SUBJECT.test(input.subject.trim())) return true;
	const address = input.from_address?.trim().toLowerCase() ?? '';
	return address !== '' && AUTOMATED_SENDER.test(address);
}

/** Sign-in alerts, codes and password mail: never clutter, always worth a look. */
export function isSecurityMail(input: {subject: string; preview: string}): boolean {
	return SECURITY_TEXT.test(`${input.subject}\n${input.preview}`);
}

/** Days a flagged or unanswered conversation has been waiting, rounded down. */
export function waitingDays(lastAt: number | null, nowMs: number): number {
	if (lastAt == null) return 0;
	return Math.max(0, Math.floor((nowMs - lastAt) / DAY_MS));
}

/** Clutter never makes the list; it goes to the clear card instead. */
export function isClutter(input: HomeFeedInput): boolean {
	if (input.security) return false;
	if (input.newsletter || input.automated) return true;
	if (input.muted && input.group) return true;
	return false;
}

function recencyBonus(lastAt: number | null, nowMs: number): number {
	if (lastAt == null) return 0;
	const ageHours = Math.max(0, (nowMs - lastAt) / HOUR_MS);
	// 24 points fresh, fading to nothing after three days.
	return Math.max(0, 24 - (ageHours / (RECENT_WINDOW_MS / HOUR_MS)) * 24);
}

export function tagsFor(input: HomeFeedInput, nowMs: number): Array<HomeFeedTag> {
	const tags: Array<HomeFeedTag> = [];
	if (input.mention) tags.push({kind: 'mention'});
	if (input.security) tags.push({kind: 'security'});
	const waiting = !input.fromMe && (input.flagged || input.unread > 0) ? waitingDays(input.lastAt, nowMs) : 0;
	if (waiting >= WAITING_TAG_MIN_DAYS) tags.push({kind: 'waiting', days: waiting});
	else if (input.flagged && !input.fromMe) tags.push({kind: 'needs_reply'});
	return tags;
}

export function scoreFor(input: HomeFeedInput, nowMs: number): number {
	let score = 0;
	if (input.mention) score += 100;
	if (input.flagged && !input.fromMe) score += 60;
	if (input.security) score += 50;
	if (input.unread > 0) score += 40 + Math.min(10, input.unread);
	if (input.muted) score -= 30;
	if (input.fromMe) score -= 10;
	return score + recencyBonus(input.lastAt, nowMs);
}

/** Does the row belong on Home at all: unread, flagged, or recent. */
export function qualifies(input: HomeFeedInput, nowMs: number): boolean {
	if (isClutter(input)) return false;
	if (input.read && input.unread === 0 && input.source === 'clickup') return false;
	if (input.unread > 0 || input.flagged || input.mention || input.security) return true;
	return input.lastAt != null && nowMs - input.lastAt <= RECENT_WINDOW_MS;
}

/** The Sorted list: what needs you first, then what is fresh. */
export function rankFeed(inputs: ReadonlyArray<HomeFeedInput>, nowMs: number): Array<HomeFeedItem> {
	return inputs
		.filter((input) => qualifies(input, nowMs))
		.map((input) => ({...input, score: scoreFor(input, nowMs), tags: tagsFor(input, nowMs)}))
		.sort((a, b) => b.score - a.score || (b.lastAt ?? 0) - (a.lastAt ?? 0) || a.name.localeCompare(b.name));
}

export type ClearGroupKind = 'newsletters' | 'automated' | 'muted_groups' | 'read_notifications';

export interface ClearGroup {
	kind: ClearGroupKind;
	items: Array<HomeFeedInput>;
}

/** What the clear card offers, grouped by the reason it can go. Empty groups are dropped. */
export function groupClearCandidates(inputs: ReadonlyArray<HomeFeedInput>): Array<ClearGroup> {
	const groups: Record<ClearGroupKind, Array<HomeFeedInput>> = {
		newsletters: [],
		automated: [],
		muted_groups: [],
		read_notifications: [],
	};
	for (const input of inputs) {
		if (input.security) continue;
		if (input.source === 'mail' && input.unread > 0) {
			if (input.newsletter) groups.newsletters.push(input);
			else if (input.automated) groups.automated.push(input);
			continue;
		}
		if (input.source === 'whatsapp' && input.muted && input.group && input.unread > 0) {
			groups.muted_groups.push(input);
			continue;
		}
		if (input.source === 'clickup' && input.read && input.unread === 0) groups.read_notifications.push(input);
		else if (input.source === 'clickup' && input.automated && input.unread > 0) groups.automated.push(input);
	}
	const order: Array<ClearGroupKind> = ['newsletters', 'automated', 'muted_groups', 'read_notifications'];
	return order.filter((kind) => groups[kind].length > 0).map((kind) => ({kind, items: groups[kind]}));
}

export function countClearCandidates(groups: ReadonlyArray<ClearGroup>): number {
	return groups.reduce((total, group) => total + group.items.length, 0);
}

/** The rows the digest card summarises: on the list but below the fold, or read and recent. */
export function digestCandidates(items: ReadonlyArray<HomeFeedItem>, shown: number): Array<HomeFeedItem> {
	return items.slice(shown);
}
